import { makeAutoObservable, runInAction } from "mobx";
import { persist } from "mobx-persist";
import {
  OpenHour,
  PlaceInterface,
  PlaceItemType,
  PlaceType,
  SubItem,
} from "../interfaces_and_types";
import {
  getPlace,
  updateItemAvailablity,
  login,
  getPlaceBudget,
  GetTranslations,
  UpdateItemPrice,
  setPlaceOpenTime,
  saveNotificationToken,
  setPlaceStatus as setPlaceStatusApi,
} from "../services";

// class Item implements PlaceItemType {
//   id: number = -1;
//   image: string = '';
//   isAvailable: boolean = false;
//   price: number = 0;
//   title: string = '';

//   constructor() {
//     makeAutoObservable(this);
//   }
// }

const isItem = (prop: PlaceItemType | SubItem): prop is PlaceItemType => {
  return (prop as PlaceItemType).subItemsCategories !== undefined;
};

export class PlaceStore implements PlaceInterface {
  place: PlaceType | null = null;
  usernameInput: string = "";
  passwordInput: string = "";
  fetchLoading: boolean = false;
  @persist language: string = "hebrew";
  token: string | null = null;
  totalBudget: number = 0;
  cashBudget: number = 0;
  cardBudget: number = 0;
  totalOrders: number = 0;
  selectedDate: Date | null = new Date();
  selectedDate2: Date | null = new Date();
  translations: any = null;
  wsConnected: boolean = false;
  usernameError: string = "";
  passwordError: string = "";
  loginError: string = "";
  notificationPopupOpen: boolean = false;
  notificationPopupMessage: string | null = null;
  statusMessage: string | null = null;
  newOrderAlertVisible: boolean = false;
  audioRef: HTMLAudioElement | null = null;
  allow_voice: boolean = false;

  SetPlaceOpenTime = async (openHour: OpenHour) => {
    const res = await setPlaceOpenTime(this.token, openHour);
    runInAction(() => {
      if (res.status === 200) {
        let openhour = this.place?.openHours.find(
          (o) => o.day === openHour.day,
        );
        if (openhour !== undefined) {
          openhour.close = openHour.close;
          openhour.open = openHour.open;
        }
      }
    });
  };

  /** Toggle restaurant available (1) vs busy (2). */
  setPlaceStatus = async (status: number) => {
    if (this.token === null || this.place === null) return false;
    const previous = this.place.status;
    // Optimistic update for snappy toggle UX
    runInAction(() => {
      if (this.place) this.place.status = status;
    });
    const res = await setPlaceStatusApi(this.token, status);
    if (res.httpStatus !== 200) {
      runInAction(() => {
        if (this.place) this.place.status = previous;
        this.statusMessage = res.message || "Something went wrong";
      });
      return false;
    }
    runInAction(() => {
      if (this.place && typeof res.status === "number" && res.status > 0) {
        this.place.status = res.status;
      }
    });
    return true;
  };

  set_allow_voice = (bool: boolean) => (this.allow_voice = bool);

  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem("deviceID");
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem("deviceID", deviceId);
    }
    return deviceId;
  }

  saveNotificationToken = async (
    notificationToken: string,
    retries: number = 3,
  ) => {
    if (this.token === null || notificationToken === "") return;

    const deviceId = this.getOrCreateDeviceId();

    for (let attempt = 0; attempt < retries; attempt++) {
      const res = await saveNotificationToken(
        this.token,
        notificationToken,
        deviceId,
      );
      if (res.status === 200) {
        localStorage.setItem("notificationToken", notificationToken);
        return;
      }
      // Don't retry on auth errors
      if (res.status === 401 || res.status === 403) return;
      console.warn(
        `Failed to save notification token (attempt ${attempt + 1}/${retries}), status:`,
        res.status,
      );
      if (attempt < retries - 1) {
        await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
      }
    }
  };

  setAudioRef_src = (src: string) => {
    if (this.audioRef !== null) this.audioRef.src = src;
  };
  setAudioRef = (audio: HTMLAudioElement | null) => (this.audioRef = audio);

  setNotificationPopupOpen = (open: boolean) =>
    (this.notificationPopupOpen = open);
  setNotificationPopupMessage = (message: string) =>
    (this.notificationPopupMessage = message);
  setStatusMessage = (message: string | null) => (this.statusMessage = message);
  setNewOrderAlertVisible = (visible: boolean) =>
    (this.newOrderAlertVisible = visible);

  setLoginError = (text: string) => (this.loginError = text);

  changeUsernameError = (text: string) => (this.usernameError = text);
  changePasswordError = (text: string) => (this.passwordError = text);

  changeWSConnected = (bool: boolean) => {
    this.wsConnected = bool;
  };

  getTranslations = async () => {
    const res = await GetTranslations(this.token);
    runInAction(() => {
      if (res.status === 200) this.translations = res.resource;
    });
    return res.resource;
  };

  GetPlaceBudget = async (periodStart: Date, periodEnd: Date) => {
    if (this.token === null) return;
    const res = await getPlaceBudget(this.token, periodStart, periodEnd);
    runInAction(() => {
      if (res.status === 200) {
        this.totalBudget = res.total;
        this.cashBudget = res.totalCash;
        this.cardBudget = res.totalCard;
        this.totalOrders = res.totalOrders;
      }
    });
  };

  Login = async () => {
    const res = await login(this.usernameInput, this.passwordInput);
    runInAction(async () => {
      if (res.status === 201 && res.token !== null) {
        this.setLoginError("");
        this.token = res.token;
        await localStorage.setItem("token", res.token);
      } else this.setLoginError("At least one of the fields is invalid");
    });
  };

  setToken = (token: string | null) => (this.token = token);

  setLanguage = (lan: string) => {
    this.language = lan;
  };

  fetchPlace = async () => {
    if (this.token === null) return;
    const data = await getPlace(this.token);
    runInAction(() => {
      if (data.status === 200 && data.place !== null) {
        this.setPlace(data.place);
      }
    });
    return data;
  };

  updateItemAvailable = async (
    bool: boolean,
    item: PlaceItemType | SubItem,
  ) => {
    if (this.token === null || this.place === null) return;
    const type = isItem(item);
    const status = await updateItemAvailablity(
      item.id,
      bool,
      type ? "item" : "subItem",
      this.token,
    );

    if (status.status === 200) {
      runInAction(() => {
        type
          ? this.changeItemIsAvailalble(bool, item)
          : this.changeSubItemIsAvailalble(bool, item);
      });
    }
  };

  updateItemPrice = async (price: number, item: PlaceItemType | SubItem) => {
    if (this.token === null || this.place === null) return;
    const type = isItem(item);
    const status = await UpdateItemPrice(
      item.id,
      price,
      type ? "item" : "subItem",
      this.token,
    );

    if (status.status === 200) {
      runInAction(() => {
        type
          ? this.changeItemPrice(price, item)
          : this.changeSubItemPrice(price, item);
      });
    }
  };

  changeItemIsAvailalble = (bool: boolean, item: PlaceItemType) =>
    (item.isAvailable = bool);

  changeItemPrice = (price: number, item: PlaceItemType) =>
    (item.price = price);

  changeSubItemIsAvailalble = (
    bool: boolean,
    subItem: SubItem | PlaceItemType,
  ) => {
    this.place?.categories.forEach((c) =>
      c.items.forEach((i) =>
        i.subItemsCategories?.forEach((s_c) =>
          s_c.subItems.forEach((s) => {
            if (s.id === subItem.id) s.isAvailable = bool;
          }),
        ),
      ),
    );
  };

  changeSubItemPrice = (price: number, subItem: SubItem | PlaceItemType) => {
    this.place?.categories.forEach((c) =>
      c.items.forEach((i) =>
        i.subItemsCategories?.forEach((s_c) =>
          s_c.subItems.forEach((s) => {
            if (s.id === subItem.id) s.price = price;
          }),
        ),
      ),
    );
  };

  changeFetchLoading = (bool: boolean) => {
    this.fetchLoading = bool;
  };

  setUsername = (text: string) => {
    this.usernameInput = text;
  };

  setPassword = (text: string) => {
    this.passwordInput = text;
  };

  setPlace = (place: PlaceType | null) => {
    this.place = place;
  };

  removePlace = () => {
    this.place = null;
  };

  setSelectedDate = (date: Date | null) => (this.selectedDate = date);
  setSelectedDate2 = (date: Date | null) => (this.selectedDate2 = date);

  constructor() {
    makeAutoObservable(this);
  }
}

const placeStore = new PlaceStore();
export default placeStore;
